import { useState, useEffect, useMemo } from "react";
import { X, Unlink, Link, Check, Loader2 } from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { fetchDomainAliases, updateDomainAliases } from "../services/api";

/**
 * Admin modal for managing domain alias overrides.
 * Shows auto-detected domain groups and allows splitting/merging.
 */
export function DomainManager({ companies, onClose, onSaved }) {
  const [aliasConfig, setAliasConfig] = useState({ merges: {}, splits: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    fetchDomainAliases()
      .then((data) => setAliasConfig({ merges: data.merges || {}, splits: data.splits || [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Build domain groups from current companies data
  const domainGroups = useMemo(() => {
    const groups = [];
    for (const company of companies) {
      if (!company.domains || company.domains.length === 0) continue;
      groups.push({
        brandRoot: company.brandRoot || null,
        displayName: company.name,
        domains: company.domains,
        contactCount: company.contacts.length,
        mergedFrom: company.mergedFrom || [],
        isMerged: (company.mergedFrom || []).length > 1 || company.domains.length > 1,
      });
    }
    return groups.filter((g) => g.domains.length > 0).sort((a, b) => b.contactCount - a.contactCount);
  }, [companies]);

  const mergedGroups = useMemo(() => domainGroups.filter((g) => g.isMerged), [domainGroups]);
  const splitRoots = new Set(aliasConfig.splits);

  const handleSplit = (brandRoot) => {
    if (!brandRoot) return;
    setAliasConfig((prev) => ({
      ...prev,
      splits: [...new Set([...prev.splits, brandRoot])],
    }));
    setDirty(true);
  };

  const handleUnsplit = (brandRoot) => {
    setAliasConfig((prev) => ({
      ...prev,
      splits: prev.splits.filter((s) => s !== brandRoot),
    }));
    setDirty(true);
  };

  const handleConfirmMerge = (group) => {
    if (!group.brandRoot) return;
    setAliasConfig((prev) => ({
      ...prev,
      merges: {
        ...prev.merges,
        [group.brandRoot]: {
          displayName: group.displayName,
          domains: group.domains,
        },
      },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDomainAliases(aliasConfig.merges, aliasConfig.splits);
      onSaved?.();
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bg,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          width: 680,
          maxHeight: "80vh",
          overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: `1px solid ${colors.border}`,
            position: "sticky",
            top: 0,
            background: colors.bg,
            zIndex: 1,
          }}
        >
          <div>
            <h2 style={{ fontFamily: fonts.heading, fontSize: 18, fontWeight: 700, color: colors.text, margin: 0 }}>
              Domain Groups
            </h2>
            <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4 }}>
              Companies auto-merged by email domain similarity. Confirm merges or split them apart.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              color: colors.textSecondary,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "16px 24px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: colors.textSecondary }}>
              <Loader2 size={20} className="spin" />            </div>
          ) : mergedGroups.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: colors.textSecondary, fontSize: 13 }}>
              No domain groups detected. Companies with related email domains will appear here automatically.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {mergedGroups.map((group) => {
                const isSplit = group.brandRoot && splitRoots.has(group.brandRoot);
                const isConfirmed = group.brandRoot && aliasConfig.merges[group.brandRoot];
                return (
                  <div
                    key={group.brandRoot || group.displayName}
                    style={{
                      border: `1px solid ${isSplit ? "rgba(239,68,68,0.3)" : isConfirmed ? "rgba(34,197,94,0.3)" : colors.border}`,
                      borderRadius: 8,
                      padding: "14px 16px",
                      background: isSplit ? "rgba(239,68,68,0.03)" : isConfirmed ? "rgba(34,197,94,0.03)" : colors.bgCard,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 14, fontFamily: fonts.heading, color: colors.text }}>
                          {group.displayName}
                        </span>
                        {group.brandRoot && (
                          <span style={{ fontSize: 11, color: colors.textSecondary, fontFamily: fonts.mono, marginLeft: 8 }}>
                            root: {group.brandRoot}
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: colors.textSecondary, marginLeft: 8 }}>
                          {group.contactCount} contacts
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {!isSplit && !isConfirmed && group.brandRoot && (
                          <button
                            onClick={() => handleConfirmMerge(group)}
                            title="Confirm this merge"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 10px",
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: fonts.body,
                              borderRadius: 5,
                              border: "1px solid rgba(34,197,94,0.3)",
                              background: "rgba(34,197,94,0.08)",
                              color: "#22c55e",
                              cursor: "pointer",
                            }}
                          >
                            <Check size={12} /> Confirm
                          </button>
                        )}
                        {isConfirmed && (
                          <span style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 10px",
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 5,
                            color: "#22c55e",
                          }}>
                            <Check size={12} /> Confirmed
                          </span>
                        )}
                        {group.brandRoot && !isSplit && (
                          <button
                            onClick={() => handleSplit(group.brandRoot)}
                            title="Split this group apart"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 10px",
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: fonts.body,
                              borderRadius: 5,
                              border: `1px solid ${colors.border}`,
                              background: "transparent",
                              color: colors.textSecondary,
                              cursor: "pointer",
                            }}
                          >
                            <Unlink size={12} /> Split
                          </button>
                        )}
                        {isSplit && (
                          <button
                            onClick={() => handleUnsplit(group.brandRoot)}
                            title="Re-merge this group"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 10px",
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: fonts.body,
                              borderRadius: 5,
                              border: "1px solid rgba(99,102,241,0.3)",
                              background: "rgba(99,102,241,0.08)",
                              color: "#6366f1",
                              cursor: "pointer",
                            }}
                          >
                            <Link size={12} /> Re-merge
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Domains */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {group.domains.map((d) => (
                        <span key={d} style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: isSplit ? "rgba(239,68,68,0.08)" : "rgba(99,102,241,0.08)",
                          color: isSplit ? colors.danger : "#6366f1",
                          fontFamily: fonts.mono,
                          textDecoration: isSplit ? "line-through" : "none",
                        }}>
                          @{d}
                        </span>
                      ))}
                    </div>

                    {/* Merged from */}
                    {group.mergedFrom.length > 1 && (
                      <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
                        Merged: {group.mergedFrom.join(" + ")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {dirty && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              padding: "16px 24px",
              borderTop: `1px solid ${colors.border}`,
              position: "sticky",
              bottom: 0,
              background: colors.bg,
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: fonts.body,
                borderRadius: 6,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.textSecondary,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: fonts.body,
                borderRadius: 6,
                border: "none",
                background: colors.accent,
                color: "#ffffff",
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
